import { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TriperService } from 'core/api/services/triper';
import { CheckinStatus } from '@utriper/core';
import { DateTime } from 'luxon';
import { Box, Typography } from '@mui/material';
import Navbar from 'components/NavBar';
import FooterComponent from 'components/Footer';
import AuthForm from './components/AuthForm';
import CheckinData from './components/CheckinData';
import CheckinForm from './components/CheckinForm';

const CheckinPage = () => {
    const initialTriperData = {
        firstName: '',
        lastName: '',
        email: '',
        dob: '',
        nationality: '',
        personalIdType: 0,
        personalId: '',
        additional: '',
        personalIdExpireDay: '',
    };
    const [loading, setLoading] = useState(false);
    const [showCheckin, setShowCheckin] = useState(false);
    const [checkinSuccess, setCheckinSuccess] = useState(false);
    const [booking, setBooking] = useState(null);

    const { t: translate } = useTranslation();
    const { search } = useLocation();
    const query = useMemo(() => new URLSearchParams(search), [search]);

    const onSubmitAuth = async (values) => {
        try {
            setLoading(true);
            const result = await TriperService.checkinAuth({
                lastName: values?.lastName?.trim() || '',
                bookingCode: values?.bookingCode?.trim() || '',
            });
            const { accessCode, status } = result?.data?.[0] || {};

            if (!accessCode) {
                throw new Error('error');
            }

            const resultBooking = await TriperService.getBooking(accessCode);

            const booking = resultBooking?.data?.[0] || {};

            if (!booking?.id) {
                throw new Error('error');
            }

            booking.tripers = [];
            if (status === CheckinStatus.IDS.initiated) {
                for (let i = 0; i < booking?.quantity || 0; i++) {
                    const triper = {
                        ...initialTriperData,
                    };

                    if (i === 0) {
                        triper.firstName = booking?.firstName || '';
                        triper.lastName = booking?.lastName || '';
                        triper.email = booking?.email || '';
                    }

                    booking.tripers.push(triper);
                }

                setShowCheckin(true);
            } else if (status === CheckinStatus.IDS.completed) {
                setCheckinSuccess(true);
            }

            setBooking({ ...booking, accessCode });
        } catch (error) {
            toast.error(error?.msg || error?.message, {
                position: 'top-right',
                theme: 'colored',
            });
        } finally {
            setLoading(false);
        }
    };

    const onSubmitCheckin = async (values) => {
        try {
            setLoading(true);
            const tripers = parseTripers(values);
            const accessCode = booking?.accessCode;

            const { data } = await TriperService.checkinTriper({ accessCode, tripers });

            if (data?.[0]?.status === CheckinStatus.IDS.completed) {
                toast.success(translate('checkin.success_message'), {
                    position: 'top-right',
                    theme: 'colored',
                });

                setCheckinSuccess(true);
            }
            setShowCheckin(false);
        } catch (error) {
            toast.error(error?.msg || error?.message, {
                position: 'top-right',
                theme: 'colored',
            });
        } finally {
            setLoading(false);
        }
    };

    const parseTripers = (booking) => {
        const tripers = [];

        for (const triperData of booking?.tripers) {
            const triper = { ...triperData };

            triper.dob = DateTime.fromJSDate(triper.dob).toFormat('yyyy-LL-dd');
            triper.personalIdExpireDay = triper.personalIdExpireDay ? DateTime.fromJSDate(triper.personalIdExpireDay).toFormat('yyyy-LL-dd') : null;

            tripers.push(triper);
        }

        return tripers;
    };

    return (
        <Box
            component="main"
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Navbar />

            {!showCheckin && !checkinSuccess ? (
                <Box component="section" sx={{ flex: 1, py: 12 }}>
                    <AuthForm
                        onSubmit={onSubmitAuth}
                        loading={loading}
                        initialValues={{
                            lastName: query.get('lastName') || null,
                            bookingCode: query.get('bookingCode') || null,
                        }}
                    />
                </Box>
            ) : (
                <Box component="section" sx={{ flex: 1, pt: 4, pb: 12 }}>
                    {(showCheckin || checkinSuccess) && <CheckinData booking={booking} isCompleted={checkinSuccess} />}
                    {showCheckin && <CheckinForm initialValues={{ tripers: booking?.tripers || [] }} onSubmit={onSubmitCheckin} loading={loading} />}

                    {checkinSuccess && (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 1,
                                mx: 'auto',
                                mt: 6,
                                px: 3,
                                boxSizing: 'border-box',
                            }}>
                            <Typography>
                                {translate('checkin.checkin_completed_helper')}
                                <a href="mailto: hello@utriper.com">hello@utriper.com</a>
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            <FooterComponent />
        </Box>
    );
};

export default CheckinPage;
